import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Result,
  Row,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography
} from "antd";
import { useTranslation } from "react-i18next";
import { calculateOffer, createOffer, prepareOffer } from "./api";
import { TRADE_LABELS, formatEuro } from "./constants";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import CustomerBranchPicker from "../../../commons/Billing/CustomerBranchPicker";
import CustomerSelect from "../../../commons/Billing/CustomerSelect";
import { findBranch } from "../../../commons/Billing/customerBranches";
import "./auftragssuche.css";

const PrepareOfferModal = ({ lead, open, onClose, onCreated }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState([]);
  const [drivers, setDrivers] = useState({});
  const [calc, setCalc] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [clientMode, setClientMode] = useState("new");
  const [clientId, setClientId] = useState(null);
  const [branchSel, setBranchSel] = useState({ clientBranch: null, billTo: "branch" });
  const [newClient, setNewClient] = useState({});
  const [quotationNumber, setQuotationNumber] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!open || !lead) return;
    setOptions(null);
    setCalc(null);
    setCreated(null);
    prepareOffer(lead.key)
      .then(res => {
        const o = res.data;
        setOptions(o);
        setSelected(o.preselected);
        setDrivers(o.values);
        setQuotationNumber(o.suggestedQuotationNumber);
        setNewClient(o.suggestedClient);
        setClientMode("new");
        setClientId(null);
        setBranchSel({ clientBranch: null, billTo: "branch" });
      })
      .catch(err => {
        generalErrorAlert(err);
        onClose();
      });
  }, [open, lead]);

  // Recalculate on the server whenever the inputs change; the newest answer wins.
  useEffect(() => {
    if (!options) return;
    const id = ++requestId.current;
    setCalculating(true);
    const timer = setTimeout(() => {
      calculateOffer(lead.key, { modules: selected, drivers })
        .then(res => id === requestId.current && setCalc(res.data))
        .catch(err => id === requestId.current && generalErrorAlert(err))
        .finally(() => id === requestId.current && setCalculating(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [options, selected, drivers]);

  const modulesByTrade = useMemo(() => {
    const groups = {};
    for (const m of options?.modules || [])
      (groups[m.tradeCode] = groups[m.tradeCode] || []).push(m);
    return groups;
  }, [options]);

  const neededDrivers = useMemo(() => {
    const keys = new Set(
      (options?.modules || []).filter(m => selected.includes(m.code)).map(m => m.driverKey)
    );
    return (options?.allDrivers || []).filter(d => keys.has(d.key));
  }, [options, selected]);

  // Until a size is entered there is nothing to calculate — a red "no positions" would be noise.
  const anySize = neededDrivers.some(d => Number(drivers[d.key]) > 0);

  const clientValid =
    clientMode === "existing"
      ? !!clientId
      : !!(newClient.firstName || "").trim() && !!(newClient.lastName || "").trim();
  const canCreate = calc && !calc.blocked && !calculating && clientValid && quotationNumber.trim();

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await createOffer(lead.key, {
        modules: selected,
        drivers,
        quotationNumber: quotationNumber.trim(),
        ...(clientMode === "existing" ? { clientId, ...branchSel } : { newClient })
      });
      setCreated(res.data.offer);
      onCreated && onCreated(res.data.offer);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    { title: t("Pos."), dataIndex: "positionNumber", width: 60, mobile: "hidden" },
    {
      title: t("Description"),
      dataIndex: "description",
      mobile: "title",
      render: (text, row) => (
        <>
          <div>{text}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.derivedFrom} · {row.laborHours} h
          </Typography.Text>
        </>
      )
    },
    {
      title: t("Quantity"),
      dataIndex: "quantity",
      align: "right",
      render: (q, row) => `${q.toLocaleString(lng)} ${row.unit}`
    },
    {
      title: t("Unit price"),
      dataIndex: "unitPrice",
      align: "right",
      render: v => formatEuro(v, lng)
    },
    {
      title: t("Total"),
      dataIndex: "total",
      align: "right",
      render: v => <Typography.Text strong>{formatEuro(v, lng)}</Typography.Text>
    }
  ];

  const body = () => {
    if (created) {
      return (
        <Result
          status="success"
          title={t("Draft offer {{number}} created", { number: created.quotationNumber })}
          subTitle={t("Check the quantities and texts in the offer before you send it.")}
          extra={[
            <Link to="/user/offers" key="offers">
              <Button type="primary">{t("Go to offers")}</Button>
            </Link>,
            <Button key="close" onClick={onClose}>
              {t("Close")}
            </Button>
          ]}
        />
      );
    }
    if (!options) return <Skeleton active />;
    if (!options.modules.length) {
      return <Empty description={t("There are no standard positions for your trades yet.")} />;
    }
    return (
      <>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t(
            "Choose standard positions and enter a few sizes. Quantities, working hours and prices are calculated from them."
          )}
        />
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Typography.Title level={5}>{t("Standard positions")}</Typography.Title>
            <Checkbox.Group value={selected} onChange={setSelected} style={{ width: "100%" }}>
              {Object.entries(modulesByTrade).map(([trade, mods]) => (
                <div key={trade} style={{ marginBottom: 8 }}>
                  <Typography.Text type="secondary">
                    {t(TRADE_LABELS[trade] || trade)}
                  </Typography.Text>
                  {mods.map(m => (
                    <div key={m.code}>
                      <Checkbox value={m.code}>
                        <Tooltip title={m.descriptionDe}>{m.nameDe}</Tooltip>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              ))}
            </Checkbox.Group>
          </Col>
          <Col xs={24} md={12}>
            <Typography.Title level={5}>{t("Sizes")}</Typography.Title>
            {options.quantitiesInText.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("Found in the request")}:{" "}
                </Typography.Text>
                {options.quantitiesInText.map((q, i) => (
                  <Tooltip key={i} title={q.context}>
                    <Tag>
                      {q.quantity} {q.unit}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            )}
            {neededDrivers.length === 0 && (
              <Typography.Text type="secondary">
                {t("Select at least one position.")}
              </Typography.Text>
            )}
            {neededDrivers.map(d => (
              <div key={d.key} className="auftrag-driver">
                <div>{d.labelDe}</div>
                <InputNumber
                  min={0}
                  max={1000000}
                  value={drivers[d.key]}
                  addonAfter={d.unit}
                  style={{ width: "100%" }}
                  onChange={v => setDrivers(prev => ({ ...prev, [d.key]: v }))}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {d.hintDe}
                </Typography.Text>
              </div>
            ))}
          </Col>
        </Row>

        <Divider orientation="start">{t("Calculation")}</Divider>
        {!anySize && (
          <Alert
            type="info"
            showIcon
            message={t("Enter at least one size to calculate the offer.")}
          />
        )}
        {anySize && calc && calc.skippedModules.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message={`${t("Skipped because the size is missing")}: ${calc.skippedModules.join(", ")}`}
          />
        )}
        {anySize && (
          <ResponsiveTable
            size="small"
            rowKey="positionNumber"
            loading={calculating}
            columns={columns}
            dataSource={calc?.lines || []}
            pagination={false}
            scroll={{ x: 600 }}
          />
        )}
        {anySize && calc && (
          <Row justify="end" style={{ marginTop: 12 }}>
            <Col xs={24} md={14}>
              <table className="auftrag-totals">
                <tbody>
                  {calc.travel > 0 && (
                    <tr>
                      <td>
                        {t("Travel (included in the prices)")}
                        {calc.travelInfo && (
                          <Typography.Text
                            type="secondary"
                            style={{ display: "block", fontSize: 12 }}
                          >
                            {calc.travelInfo.trips} × 2 × {calc.travelInfo.distanceKm} km
                            {calc.travelInfo.approximate
                              ? ` (${t("location estimated from postcode")})`
                              : ""}
                          </Typography.Text>
                        )}
                      </td>
                      <td>{formatEuro(calc.travel, lng)}</td>
                    </tr>
                  )}
                  <tr>
                    <td>{t("Net")}</td>
                    <td>{formatEuro(calc.net, lng)}</td>
                  </tr>
                  <tr>
                    <td>
                      {t("VAT")} ({calc.vatPct} %)
                    </td>
                    <td>{formatEuro(calc.vat, lng)}</td>
                  </tr>
                  <tr className="auftrag-totals-grand">
                    <td>{t("Gross")}</td>
                    <td>{formatEuro(calc.gross, lng)}</td>
                  </tr>
                  <tr>
                    <td>{t("Margin")}</td>
                    <td>
                      {calc.marginPct.toLocaleString(lng)} % ({t("target")}{" "}
                      {calc.targetMarginPct.toLocaleString(lng)} %)
                    </td>
                  </tr>
                  <tr>
                    <td>{t("Working hours")}</td>
                    <td>{calc.laborHours.toLocaleString(lng)} h</td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        )}
        {anySize &&
          calc &&
          calc.checks.map((c, i) => (
            <Alert
              key={i}
              type={
                c.severity === "BLOCKER"
                  ? c.code === "NO_SOURCE_QUANTITIES"
                    ? "warning"
                    : "error"
                  : c.severity === "WARNING"
                    ? "warning"
                    : "info"
              }
              showIcon
              style={{ marginTop: 8 }}
              message={c.message}
            />
          ))}

        <Divider orientation="start">{t("Offer")}</Divider>
        <Row gutter={[16, 12]}>
          <Col xs={24} md={8}>
            <div>{t("Offer Number")}</div>
            <Input value={quotationNumber} onChange={e => setQuotationNumber(e.target.value)} />
          </Col>
          <Col xs={24} md={16}>
            <div>{t("Client")}</div>
            <Radio.Group value={clientMode} onChange={e => setClientMode(e.target.value)}>
              <Radio value="new">{t("New client")}</Radio>
              <Radio value="existing" disabled={!options.clients.length}>
                {t("Existing client")}
              </Radio>
            </Radio.Group>
          </Col>
        </Row>
        <div style={{ marginTop: 12 }}>
          {clientMode === "existing" ? (
            <>
              <CustomerSelect
                style={{ width: "100%" }}
                placeholder={t("Select client")}
                customers={options.clients}
                describe={c => c.city || ""}
                value={clientId}
                branch={branchSel.clientBranch}
                onChange={(id, branchId, customer) => {
                  setClientId(id);
                  setBranchSel({
                    clientBranch: branchId,
                    billTo: (branchId && findBranch(customer, branchId)?.defaultBillTo) || "branch"
                  });
                }}
              />
              <Form layout="vertical" style={{ marginTop: 12 }}>
                <CustomerBranchPicker
                  customer={options.clients.find(c => String(c._id) === String(clientId))}
                  value={branchSel}
                  onChange={setBranchSel}
                  showBranchSelect={false}
                />
              </Form>
            </>
          ) : (
            <Row gutter={[12, 12]}>
              {[
                ["company", t("Company / authority")],
                ["firstName", t("First Name")],
                ["lastName", t("Last Name")],
                ["address", t("Address")],
                ["zipCode", t("Zip Code")],
                ["city", t("City")]
              ].map(([field, label]) => (
                <Col
                  xs={24}
                  sm={field === "company" || field === "address" ? 24 : 12}
                  md={field === "company" || field === "address" ? 24 : 8}
                  key={field}
                >
                  <Input
                    placeholder={label}
                    value={newClient[field]}
                    status={
                      (field === "firstName" || field === "lastName") &&
                      !(newClient[field] || "").trim()
                        ? "warning"
                        : undefined
                    }
                    onChange={e => setNewClient(prev => ({ ...prev, [field]: e.target.value }))}
                  />
                </Col>
              ))}
            </Row>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal
      centered
      width={960}
      className="auftrag-offer-modal"
      open={open}
      title={lead ? `${t("Prepare offer")}: ${lead.title}` : t("Prepare offer")}
      onCancel={onClose}
      destroyOnClose
      footer={
        created
          ? null
          : [
              <Button key="cancel" onClick={onClose}>
                {t("Cancel")}
              </Button>,
              <Button
                key="create"
                type="primary"
                loading={creating}
                disabled={!canCreate}
                onClick={handleCreate}
              >
                {t("Create draft offer")}
              </Button>
            ]
      }
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        {body()}
      </Space>
    </Modal>
  );
};

export default PrepareOfferModal;
